import { useEffect, useState, useContext, useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { Body } from '@natomalabs/natomui/src/components/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {
  ChevronUpIcon,
  LogOutIcon,
  MonitorIcon,
  MoonIcon,
  SettingsIcon,
  SunIcon
} from '@natomalabs/natomui/src/icons';
import { useRouter } from 'next/navigation';
import { LogoutPath, ProfileSettingsPathApiKey } from '../lib/paths';
import { MeContext } from '../providers/Me';
import UserAvatar from './UserAvatar';
import { BillingFeatureId, type User } from '../../__generated__/graphql';
import useIsBillingFeatureEnabled from '../hooks/use_billing_feature';

type ThemeMode = 'system' | 'light' | 'dark';
const THEME_MODE_CYCLE: Record<ThemeMode, ThemeMode> = {
  system: 'light',
  light: 'dark',
  dark: 'system'
};
const THEME_MODE_LABEL: Record<ThemeMode, string> = {
  system: 'System',
  light: 'Light',
  dark: 'Dark'
};

function ThemeModeIcon({ mode }: { mode: ThemeMode }) {
  if (mode === 'light') return <SunIcon />;
  if (mode === 'dark') return <MoonIcon />;
  return <MonitorIcon />;
}

export default function ProfileButton() {
  const router = useRouter();

  const { mode, setMode } = useColorScheme();
  const currentMode: ThemeMode = (mode as ThemeMode | undefined) ?? 'system';

  const { currentUser: currentUserData } = useContext(MeContext);
  const isBillingFeatureEnabled = useIsBillingFeatureEnabled();
  const [currentUserName, setCurrentUserName] = useState<string | undefined>(
    undefined
  );
  const [currentUserEmail, setCurrentUserEmail] = useState<string | undefined>(
    undefined
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const isAiFeatureEnabled = useMemo(() => {
    return isBillingFeatureEnabled(BillingFeatureId.AiIdentity);
  }, [isBillingFeatureEnabled]);

  useEffect(() => {
    if (currentUserData) {
      setCurrentUserEmail(currentUserData.email);
      if (currentUserData.displayName) {
        setCurrentUserName(currentUserData.displayName);
      }
    }
  }, [currentUserData]);

  return (
    <Box display="flex" alignItems="center" justifyContent="center">
      <Button
        id="postioned-button"
        aria-controls={open ? 'positioned-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        fullWidth
        sx={(theme) => ({
          display: 'flex',
          gap: theme.spacing(1),
          px: theme.spacing(1.5),
          py: theme.spacing(1),
          borderRadius: theme.spacing(2),
          border: 0,
          justifyContent: 'space-between',
          color: theme.palette.actionable.secondary.default.foreground,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor:
              theme.palette.actionable.secondary.active.background
          }
        })}
      >
        {(currentUserEmail || currentUserName) && (
          <UserAvatar
            user={currentUserData as User}
            spacing={3.5}
            TextComponent={Body}
          />
        )}
        <ChevronUpIcon />
      </Button>
      <Menu
        id="positioned-menu"
        aria-labelledby="positioned-button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              p: theme.spacing(1),
              width: anchorEl && anchorEl.offsetWidth
            })
          }
        }}
        sx={(theme) => ({
          mt: theme.spacing(-0.5)
        })}
      >
        <MenuItem
          onClick={() => {
            setMode(THEME_MODE_CYCLE[currentMode]);
          }}
        >
          <ThemeModeIcon mode={currentMode} />
          <Typography>{THEME_MODE_LABEL[currentMode]} theme</Typography>
        </MenuItem>
        {isAiFeatureEnabled && (
          <MenuItem
            onClick={() => {
              handleClose();
              router.push(ProfileSettingsPathApiKey);
            }}
          >
            <SettingsIcon />
            <Typography>API Keys</Typography>
          </MenuItem>
        )}
        <MenuItem
          sx={(theme) => ({
            gap: theme.spacing(1.25)
          })}
          onClick={() => {
            handleClose();
            router.push(LogoutPath);
          }}
        >
          <LogOutIcon />
          <Typography>Logout</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
}
